import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { BookingSection } from "@/components/BookingSection";
import { CheckCircle, ArrowRight, Shield, Clock, FileCheck, Users, Star, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

// Modern building with curved balconies
const PACKAGES_HERO_BG = "https://images.unsplash.com/photo-1758526116315-361575027838?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBza3lzY3JhcGVyJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzczMDcyODU1fDA&ixlib=rb-4.1.0&q=85";
// Scales of justice
const GUARANTEES_BG = "https://images.pexels.com/photos/6077520/pexels-photo-6077520.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const packages = [
  {
    id: "consultation",
    name: "Property Investment Consultation",
    tagline: "Initial Advisory",
    description: "Private consultation for investors seeking legal clarity before purchasing property in Nigeria.",
    price: "₦200,000 / £100",
    priceNote: null,
    timeline: null,
    features: [
      "Property investment guidance",
      "Legal process explanation",
      "Preliminary title review",
      "Risk identification"
    ],
    note: "Consultation fees are credited toward full legal engagement if the client proceeds with our services.",
    ideal: null,
    highlight: false,
    type: "fixed"
  },
  {
    id: "essential",
    name: "Essential Due Diligence",
    tagline: "For informed investment decisions",
    description: "Comprehensive property verification to ensure your investment is legally sound before you commit.",
    price: "From ₦750,000",
    priceNote: "(£400 approx.)",
    timeline: "5-7 business days",
    features: [
      "Title verification",
      "Ownership history investigation",
      "Encumbrance and litigation checks",
      "Written legal opinion report"
    ],
    note: null,
    ideal: null,
    highlight: false,
    type: "fixed"
  },
  {
    id: "comprehensive",
    name: "Comprehensive Advisory",
    tagline: "Full transaction support",
    description: "End-to-end legal representation from property verification through acquisition completion.",
    price: "From ₦1,800,000",
    priceNote: "(£950 approx.)",
    timeline: "2-4 weeks",
    features: [
      "Full due diligence investigation",
      "Contract drafting and review",
      "Negotiation support",
      "Transaction structuring",
      "Closing coordination"
    ],
    note: null,
    ideal: null,
    highlight: true,
    type: "fixed"
  },
  {
    id: "premium",
    name: "Premium Acquisition Advisory",
    tagline: "Complete investment support",
    description: "Specialized legal services for high-value property acquisitions requiring comprehensive oversight.",
    price: "From ₦4,500,000",
    priceNote: "(£2,400 approx.)",
    timeline: "Ongoing support",
    features: [
      "End-to-end property acquisition advisory",
      "Developer due diligence",
      "Investment structuring",
      "Transaction supervision",
      "Closing oversight"
    ],
    note: null,
    ideal: null,
    highlight: false,
    type: "fixed"
  },
  {
    id: "sale-representation",
    name: "Property Sale Representation",
    tagline: "For property sellers",
    description: "We represent property owners in sourcing qualified buyers and completing secure property transactions.",
    price: "10% of final sale price",
    priceNote: "Minimum: ₦2,500,000",
    timeline: null,
    features: [
      "Buyer sourcing through investor networks",
      "Property marketing and exposure",
      "Negotiation support",
      "Legal documentation and contract structuring",
      "Transaction coordination through closing"
    ],
    note: "Professional fees become payable only upon successful completion of the transaction.",
    ideal: null,
    highlight: false,
    type: "percentage"
  },
  {
    id: "buyer-available",
    name: "Buyer Already Available",
    tagline: "Transaction advisory only",
    description: "Where a client already has a buyer and requires legal transaction advisory only.",
    price: "3% of property value",
    priceNote: null,
    timeline: null,
    features: [
      "Contract preparation and review",
      "Transaction structuring",
      "Legal documentation",
      "Closing coordination"
    ],
    note: null,
    ideal: null,
    highlight: false,
    type: "percentage"
  }
];

const guarantees = [
  {
    icon: Shield,
    title: "Confidentiality Assured",
    description: "All client matters are handled with strict confidentiality and professional discretion."
  },
  {
    icon: FileCheck,
    title: "Thorough Documentation",
    description: "Every finding is documented in detailed reports you can rely on for decision-making."
  },
  {
    icon: Clock,
    title: "Timely Delivery",
    description: "We respect your timeline and deliver within agreed timeframes."
  },
  {
    icon: Users,
    title: "Direct Partner Access",
    description: "Work directly with experienced legal professionals, not junior associates."
  }
];

export default function PackagesPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <main className="min-h-screen bg-[#FAFAF9]" data-testid="packages-page">
      <Navigation scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden border-b border-[#E5E5E5]">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${PACKAGES_HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-white/95" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-[#D4AF37] text-sm tracking-[0.25em] uppercase font-medium">
              Consultation Packages
            </span>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] mt-4 mb-6" data-testid="packages-title">
              Legal Advisory Packages for
              <span className="block text-[#D4AF37]">Discerning Investors</span>
            </h1>
            <p className="text-[#1A1A1A]/60 text-lg leading-relaxed">
              Our structured advisory packages provide clarity on scope, timeline, and investment—ensuring 
              you receive the precise legal support your property transaction requires.
            </p>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 md:py-24 lg:py-32" data-testid="packages-grid">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          {/* Main Packages - 2x2 Grid */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-8">
            {packages.slice(0, 4).map((pkg) => (
              <div
                key={pkg.id}
                className={`relative flex flex-col p-6 md:p-8 lg:p-10 border transition-all ${
                  pkg.highlight
                    ? "bg-[#0A0A0B] border-[#D4AF37] text-white"
                    : "bg-white border-[#E5E5E5] hover:border-[#D4AF37]/50"
                }`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0A0A0B] px-4 py-1 text-xs font-bold tracking-wider uppercase">
                    Most Popular
                  </div>
                )}

                <div className="mb-5 md:mb-6">
                  <p className="text-[#D4AF37] text-xs md:text-sm tracking-wider uppercase mb-2">
                    {pkg.tagline}
                  </p>
                  <h3 className={`font-['Playfair_Display'] text-xl md:text-2xl mb-3 ${pkg.highlight ? "text-white" : "text-[#1A1A1A]"}`}>
                    {pkg.name}
                  </h3>
                  <p className={`text-sm leading-relaxed ${pkg.highlight ? "text-white/70" : "text-[#1A1A1A]/60"}`}>
                    {pkg.description}
                  </p>
                </div>

                <div className="mb-5 md:mb-6 pb-5 md:pb-6 border-b border-white/10">
                  <p className={`font-['Playfair_Display'] text-2xl md:text-3xl ${pkg.highlight ? "text-white" : "text-[#1A1A1A]"}`}>
                    {pkg.price}
                  </p>
                  {pkg.priceNote && (
                    <p className={`text-sm mt-1 ${pkg.highlight ? "text-white/50" : "text-[#1A1A1A]/50"}`}>
                      {pkg.priceNote}
                    </p>
                  )}
                  {pkg.timeline && (
                    <p className={`text-sm mt-1 ${pkg.highlight ? "text-white/50" : "text-[#1A1A1A]/50"}`}>
                      Timeline: {pkg.timeline}
                    </p>
                  )}
                </div>

                <ul className="space-y-2 md:space-y-3 mb-6 flex-grow">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 md:gap-3">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#D4AF37]" />
                      <span className={`text-sm ${pkg.highlight ? "text-white/80" : "text-[#1A1A1A]/70"}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {pkg.note && (
                  <p className={`text-xs italic mb-6 p-3 border ${pkg.highlight ? "text-white/60 border-white/10 bg-white/5" : "text-[#1A1A1A]/50 border-[#D4AF37]/20 bg-[#D4AF37]/5"}`}>
                    {pkg.note}
                  </p>
                )}

                <a
                  href="#booking"
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 md:py-4 text-sm tracking-[0.1em] font-bold uppercase transition-all ${
                    pkg.highlight
                      ? "bg-[#D4AF37] text-[#0A0A0B] hover:bg-[#C5A028]"
                      : "bg-[#1A1A1A] text-white hover:bg-[#0A0A0B]"
                  }`}
                  data-testid={`package-${pkg.id}-cta`}
                >
                  Select Package
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>

          {/* Property Sale Services - 2 Column */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
            {packages.slice(4, 6).map((pkg) => (
              <div
                key={pkg.id}
                className="relative flex flex-col p-6 md:p-8 lg:p-10 border border-[#E5E5E5] bg-white hover:border-[#D4AF37]/50 transition-all"
                data-testid={`package-${pkg.id}`}
              >
                <div className="mb-5 md:mb-6">
                  <p className="text-[#D4AF37] text-xs md:text-sm tracking-wider uppercase mb-2">
                    {pkg.tagline}
                  </p>
                  <h3 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1A] mb-3">
                    {pkg.name}
                  </h3>
                  <p className="text-sm text-[#1A1A1A]/60 leading-relaxed">
                    {pkg.description}
                  </p>
                </div>

                <div className="mb-5 md:mb-6 pb-5 md:pb-6 border-b border-[#E5E5E5]">
                  <p className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A]">
                    {pkg.price}
                  </p>
                  {pkg.priceNote && (
                    <p className="text-sm mt-1 text-[#D4AF37] font-semibold">
                      {pkg.priceNote}
                    </p>
                  )}
                </div>

                <ul className="space-y-2 md:space-y-3 mb-6 flex-grow">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 md:gap-3">
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#D4AF37]" />
                      <span className="text-sm text-[#1A1A1A]/70">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {pkg.note && (
                  <p className="text-xs italic mb-6 p-3 border border-[#D4AF37]/20 bg-[#D4AF37]/5 text-[#1A1A1A]/60">
                    {pkg.note}
                  </p>
                )}

                <a
                  href="#booking"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 md:py-4 text-sm tracking-[0.1em] font-bold uppercase bg-[#1A1A1A] text-white hover:bg-[#0A0A0B] transition-all"
                  data-testid={`package-${pkg.id}-cta`}
                >
                  Enquire Now
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>

          {/* Main CTA */}
          <div className="text-center pt-8 md:pt-12 border-t border-[#E5E5E5]">
            <p className="text-[#1A1A1A]/50 text-sm mb-6">
              Ready to discuss your property investment needs?
            </p>
            <a
              href="#booking"
              className="inline-flex items-center justify-center gap-2 md:gap-3 bg-[#D4AF37] text-[#0A0A0B] px-8 md:px-10 py-4 md:py-5 text-sm tracking-wide font-bold hover:bg-[#C5A028] transition-all"
              data-testid="packages-main-cta"
            >
              Book a Private Consultation
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <p className="text-center text-[#1A1A1A]/40 text-xs mt-8">
            Fees may vary depending on property value, location, and transaction complexity. Contact us for a detailed quote.
          </p>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-24 lg:py-32 relative overflow-hidden border-t border-[#E5E5E5]" data-testid="packages-guarantees">
        {/* Background with scales of justice */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${GUARANTEES_BG})` }}
        />
        <div className="absolute inset-0 bg-white" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D4AF37] text-sm tracking-[0.25em] uppercase font-medium">
              Our Commitment
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#1A1A1A] mt-4 mb-6">
              The 4 ACES Standard
            </h2>
            <p className="text-[#1A1A1A]/60 text-lg">
              Every engagement is underpinned by our commitment to excellence, 
              discretion, and client-focused service.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {guarantees.map((item, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-semibold text-[#1A1A1A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#1A1A1A]/60">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <BookingSection />

      {/* Final CTA */}
      <section className="py-20 bg-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-white mb-4">
            Need a Custom Engagement?
          </h2>
          <p className="text-white/60 mb-8">
            For complex transactions or unique requirements, contact us to discuss a tailored advisory arrangement.
          </p>
          <a
            href="https://wa.me/2348032143826?text=Hello%204ACES%20Attorneys%2C%20I%20saw%20your%20website%20and%20would%20like%20to%20discuss%20a%20property%20transaction%20in%20Nigeria."
            className="inline-flex items-center gap-2 text-[#D4AF37] font-medium hover:gap-3 transition-all"
          >
            <MessageCircle className="w-4 h-4" />
            Discuss Your Requirements
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}
